/**
 * AuthContext module.
 * 
 * Provides the AuthProvider React component and the useAuth hook.
 * AuthProvider is responsible for fetching and storing the user session 
 * info and checking session expiration.
 * 
 * @module AuthContext
 */

import React, { createContext, useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { getActiveSessionURL, getNavigationConfigURL } from '../utils/envVals';
import { loginRedirect } from '../utils/loginRedirect';
import Loader from '../components/Loader';
import { IUserContext } from '../interfaces/userContext';
// Remove dependency on sessionStorage-based functions.
// import { isUserSessionActive } from '../utils/utils';

interface AuthContextType {
    userContext: IUserContext | null;
    setUserContext: React.Dispatch<React.SetStateAction<IUserContext | null>>;
}

export const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
    children: React.ReactNode;
}

/**
 * AuthProvider component.
 * 
 * This component fetches the active session and verifies the session
 * expiration. If the session is expired, it triggers a login redirect.
 *
 * @param {AuthProviderProps} props - children elements
 */

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    // Initialize the user context to null.
    const [userContext, setUserContext] = useState<any | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        const checkSession = async () => {
            try {
                const response = await fetch(getActiveSessionURL());
                const newUserSession = await response.json();
                const { userProfile, active, secondsToExpiration } = newUserSession;

                if (userProfile && active) {
                    // Calculate the expiry timestamp.
                    const viSsnExp = new Date().getTime() + secondsToExpiration * 1000;
                    const navConfigRes = await fetch(getNavigationConfigURL());
                    const navConfig = await navConfigRes.json();
                    const userObjStr = JSON.stringify(userProfile);
                    sessionStorage.setItem('userProfile', userObjStr);
                    setUserContext({
                        userProfile,
                        tenant: 'sams',
                        navConfig,
                        viSsnExp,
                    });
                } else {
                    console.warn('Invalid session', { active });
                    sessionStorage.clear();
                    localStorage.clear();
                    loginRedirect(window.location.href);
                }
            } catch (error) {
                console.error('Error checking session:', error);
            }
        };

        // If we already have a userContext, check the expiry timestamp.
        if (userContext && typeof userContext.viSsnExp === 'number') {
            if (Date.now() > userContext.viSsnExp) {
                // Session expired—force login.
                loginRedirect(window.location.href);
                return;
            }
        } else {
            // No userContext means we need to check the session.
            checkSession();
        }
    }, [navigate, userContext]);

    console.log('=>', { userContext })
    if (!userContext) {
        return <Loader />
    }
    // If the userContext is not set, return null or a loading spinner.

    return (
        <AuthContext.Provider value={{ userContext, setUserContext }}>
            {children}
        </AuthContext.Provider>
    );
};
/**
 * Custom hook to access authentication context.
 */
export const useAuth = () => useContext(AuthContext);